import React, { useState, useEffect, useRef } from "react";
import { getFirestore, collection, query, limit, where, orderBy, getDocs, addDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useAuth } from '../context/AuthContext.js';
import { useNavigate } from "react-router-dom";
import "./VacantesSlider.css";

const VacantesSlider = () => {
  const db = getFirestore();
  const auth = getAuth();
  const { currentUser } = useAuth();
  const sliderRef = useRef(null);
  const navigate = useNavigate();
  const [vacantes, setVacantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVacante, setSelectedVacante] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (currentUser !== undefined) {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    const fetchVacantes = async () => {
      const collectionRef = collection(db, "vacantes");
  
      // Crear consulta para filtrar vacantes con estado "iniciada" y fechaCierre posterior a la actual
      const q = query(
        collectionRef,
        where("estado", "==", "iniciada"),
        where("fechaCierre", ">", new Date()),
        orderBy("createdAt", "desc"), // Opcional: ordena por fecha de cierre
        limit(20)
      );
  
      try {
        const snapshot = await getDocs(q);
  
        const fetchedVacantes = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate().toLocaleString(), // Convierte createdAt
            fechaCierre: data.fechaCierre?.toDate().toLocaleDateString(), // Convierte fechaCierre
          };
        });
         
        setVacantes(fetchedVacantes);
      } catch (error) {
        console.error("Error al obtener vacantes: ", error);
      } finally {
        setLoading(false);
      }
    };
  
    const fetchUserProfile = async () => {
      const user = auth.currentUser;
  
      if (user) {
        const profileRef = collection(db, "users");
        const snapshot = await getDocs(profileRef);
  
        const profileDoc = snapshot.docs.find((doc) => doc.id === user.uid);
        if (profileDoc) {
          setProfile(profileDoc.data()); // Esto incluye el campo `role` si existe en el documento
        }
      }
    };
  
    fetchVacantes();
    fetchUserProfile();
  }, [db, auth]);
  

  const handleScroll = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = direction === "next" ? 300 : -300;
      sliderRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const calculateMatch = (vacante) => {
    if (!profile) return false;

    const weights = {
      skills: 0.5,
      ubicacion: 0.2,
      salario: 0.2,
      experiencia: 0.1,
    };

    let score = 0;

    if (profile.skills && vacante.skillsRequeridas) {
      const skillsMatch = profile.skills
        .split(",")
        .map(skill => skill.trim().toLowerCase())
        .some(skill => vacante.skillsRequeridas.includes(skill));
      if (skillsMatch) score += weights.skills;
    }

    if (profile.ubicacion && vacante.ubicacion) {
      const ubicacionMatch = profile.ubicacion.toLowerCase() === vacante.ubicacion.toLowerCase();
      if (ubicacionMatch) score += weights.ubicacion;
    }

    if (profile.salarioDeseado && vacante.salario) {
      const salarioMatch = vacante.salario >= profile.salarioDeseado;
      if (salarioMatch) score += weights.salario;
    }

    if (profile.experiencia && vacante.experienciaRequerida) {
      const experienciaMatch = parseInt(profile.experiencia, 10) >= parseInt(vacante.experienciaRequerida, 10);
      if (experienciaMatch) score += weights.experiencia;
    }

    const matchThreshold = 0.7;
    return score >= matchThreshold;
  };

  const handlePostulate = (vacante) => {
    if (currentUser) {
      setSelectedVacante(vacante); // Abre el modal si el usuario está autenticado
    } else {
      navigate("/login"); // Redirige al login si no está autenticado
    }
  };

  const closeModal = () => {
    setSelectedVacante(null);
  };

  const createPostulacion = async (vacanteId) => {
    if (!currentUser) {
      alert("Debes iniciar sesión para postularte.");
      return;
    }
  
    if (profile?.role !== "postulante") { // Asegúrate de verificar el `role` desde el perfil del usuario
      alert(
        "Necesitas crear una oficina para postularse y recibir solicitudes de empleo y completar tu currículum para poder postularte."
      );
      return;
    }
  
    try {
      // Obtener el `fullName` del usuario autenticado desde Firestore
      const userDocRef = collection(db, "users");
      const snapshot = await getDocs(userDocRef);
  
      const userDoc = snapshot.docs.find((doc) => doc.id === currentUser.uid);
      const userData = userDoc?.data();
      const fullName = userData?.fullName || "Nombre no disponible";
  
      const postulacionData = {
        userId: currentUser.uid,
        nombre: fullName, // Usar el nombre obtenido de Firestore
        vacanteId,
        createdAt: new Date(),
        status: "Pendiente",
      };
  
      await addDoc(collection(db, "postulaciones"), postulacionData);
      alert("Postulación enviada con éxito.");
      closeModal();
    } catch (error) {
      console.error("Error al enviar la postulación:", error);
      alert("Hubo un error al enviar la postulación. Inténtalo de nuevo.");
    }
  };

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=https://apimys.com/?referrer=${currentUser?.uid || ''}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=https://apimys.com/?referrer=${currentUser?.uid || ''}`,
    x: `https://x.com/intent/tweet?text=Encontrá%20las%20mejores%20vacantes%20en%20Apimys!%20https://apimys.com/?referrer=${currentUser?.uid || ''}`,
    whatsapp: `https://api.whatsapp.com/send?text=Encontrá%20las%20mejores%20vacantes%20en%20Apimys!%20https://apimys.com/?referrer=${currentUser?.uid || ''}`,
  };

  if (loading) {
    return <div className="loading">Cargando vacantes...</div>;
  }

  if (vacantes.length === 0) {
    return <div className="no-vacantes">Verás vacantes aquí muy pronto.</div>;
  }

  return (
    <div className="vacantes-slider">
      <h2 className="title">Vacantes Disponibles</h2>
      <div className="slider-container">
        <button className="slider-button prev" onClick={() => handleScroll("prev")}>
          &#8249;
        </button>
        <div className="sliderV" ref={sliderRef}>
          {vacantes.map((vacante) => (
            <div className="vacante-card" key={vacante.id}>
              <h3 className="vacante-title">{vacante.titulo}</h3>
              <p className="vacante-ubicacion">
                <strong>Ubicación:</strong> {vacante.ubicacion}
              </p>
              <p className="vacante-salario">
                <strong>Salario:</strong> ${vacante.salario}{vacante.moneda}
              </p>
              {calculateMatch(vacante) && (
                <p className="vacante-match">✅ Coincide con tu perfil</p>
              )}
              <div className="iconos-refs">
                <ul className="wrapper-icons sci">
                  <li className="icon comentarios">
                    <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer">
                      <span><i className="fa-brands fa-facebook"></i></span>
                    </a>
                  </li>
                  <li className="icon comentarios">
                    <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer">
                      <span><i className="fa-brands fa-linkedin"></i></span>
                    </a>
                  </li>
                  <li className="icon comentarios">
                    <a href={shareLinks.x} target="_blank" rel="noopener noreferrer">
                      <span><i className="fa-brands fa-x-twitter"></i></span>
                    </a>
                  </li>
                  <li className="icon comentarios">
                    <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer">
                      <span><i className="fa-brands fa-whatsapp"></i></span>
                    </a>
                  </li>
                </ul>
              </div>
              <button className="btn-postulate" onClick={() => handlePostulate(vacante)}>
                Postularse
              </button>
            </div>
          ))}
        </div>
        <button className="slider-button next" onClick={() => handleScroll("next")}>
          &#8250;
        </button>
      </div>

      {selectedVacante && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="container-modal-postulares">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{selectedVacante.titulo}</h2>
              <p>{selectedVacante.descripcion}</p>
              <p>
                <strong>Ubicación:</strong> {selectedVacante.ubicacion}
              </p>
              <p>
                <strong>Experiencia requerida:</strong> {selectedVacante.nivelExperiencia}
              </p>
              <p>
                <strong>Habilidades requeridas:</strong> {selectedVacante.habilidades}
              </p>
              <p>
                <strong>Horario requerido:</strong> {selectedVacante.tipoContrato}
              </p>
              <p>
                <strong>Salario:</strong> ${selectedVacante.salario}{selectedVacante.moneda}
              </p>
              <p>
                <strong>Beneficios:</strong> {selectedVacante.beneficios}
              </p>
              <p>
                <strong>Postulate hasta:</strong> {selectedVacante.fechaCierre}
              </p>
              <button className="btn-postulate" onClick={() => createPostulacion(selectedVacante.id)}>
                Confirmar Postulación
              </button>
              <button className="btn btn-secondary" onClick={closeModal}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VacantesSlider;
